import 'regenerator-runtime/runtime'

import * as THREE from 'three'
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer.js'
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass.js'
import { OutlinePass } from 'three/examples/jsm/postprocessing/OutlinePass.js'
import CameraControls from 'camera-controls'
import CONST from './constant'
import Scene from './testScene/scene'
import ViewerScene from './viewerScene'
import store from '~/js/store'
import { TweenMax } from "gsap/all"; 
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls'
import Config from './viewerConfig'

CameraControls.install( { THREE: THREE } );
const EPS = 1e-5;
const ENABLE_CAMERA_CONTROL = true;

class Renderer{
  constructor(){
  }

  async init(){
    this.bFristFrame = true;
    this.bRender = true;

    // add events
    window.addEventListener( 'resize', this.onWindowResize.bind(this), false );
    window.addEventListener( 'mousemove', this.onMouseMove.bind(this));
    store.watch(store.getters['material/getIsShowMaterial'], this.onSelectMaterial.bind(this));

    // setup renderer
    this.renderer = new THREE.WebGLRenderer( { antialias: true } );
    this.renderer.setPixelRatio( window.devicePixelRatio );
    this.renderer.setSize( window.innerWidth, window.innerHeight );
    this.renderer.outputEncoding = THREE.sRGBEncoding;
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    this.renderer.info.autoReset = false;
    this.renderer.setClearColor( 0x999999, 1);
    document.body.appendChild( this.renderer.domElement );


    // create camera
    this.camera = new THREE.PerspectiveCamera( 45, window.innerWidth/window.innerHeight, 0.25, 10000);
    this.camera.fov = 50;
    const camPos = new THREE.Vector3(4, 13, 0);
    this.camera.position.set(camPos.x, camPos.y, camPos.z);

    // create scene
    this.viewerConfig = Config.WebGLTest2;
    this.scene = new ViewerScene(this.viewerConfig);
    await this.scene.init();

    // camera controls
    this.cameraControls = new CameraControls( this.camera, this.renderer.domElement );
    this.cameraControls.mouseButtons.right = CameraControls.ACTION.NONE; // disable move camera
    this.cameraControls.mouseButtons.wheel = CameraControls.ACTION.NONE; // use zoom instead of ZOOM
    this.cameraControls.touches.two = CameraControls.ACTION.NONE;  // zoom only (disable move camera)
    this.cameraControls.azimuthRotateSpeed = - 0.3; // negative value to invert rotation direction
    this.cameraControls.polarRotateSpeed   = - 0.3; // negative value to invert rotation direction
    this.cameraControls.draggingDampingFactor = 1;
    this.cameraControls.dampingFactor = 1;
    this.cameraControls.truckSpeed = 1 / EPS * 3;
    this.cameraControls.setTarget(camPos.x, camPos.y, camPos.z+EPS);
    this.cameraControls.saveState();
    this.cameraControls.addEventListener('controlstart', this.onControlEvent.bind(this));
    this.cameraControls.addEventListener('control', this.onControlEvent.bind(this));
    this.cameraControls.addEventListener('controlend', this.onControlEvent.bind(this));
    const p = this.scene.getDefaultCameraPosition() || new THREE.Vector3(4, 3, 0);
    this.cameraControls.moveTo(p.x, p.y, p.z, false);

    if(!ENABLE_CAMERA_CONTROL){
      this.orbitControls = new OrbitControls( this.camera, this.renderer.domElement);
      this.orbitControls.minDistance = 2;
      this.orbitControls.maxDistance = 10000;
      this.orbitControls.target.set( 0, 0, 0 );
    }

    // raycaster
    this.bWillRaycast = false;
    this.rayCaster = new THREE.Raycaster();
    this.selectedObjects = [];
    this.choosedObject = null;
    this.currentMousePosition = null;

    
    
    
   
    // create effect composer
    this.composer = new EffectComposer(this.renderer);
    this.setupRenderPass();

    // clock
    this.clock = new THREE.Clock();
    
    this.materialList = {};
    this.materialList[`${CONST.MATERIAL.RED}`] = new THREE.MeshLambertMaterial({color: 0xff0000});
    this.materialList[`${CONST.MATERIAL.GREEN}`] = new THREE.MeshLambertMaterial({color: 0x00ff00});
    this.materialList[`${CONST.MATERIAL.BLUE}`] = new THREE.MeshLambertMaterial({color: 0x0000ff});
    // execute render loop
    this.render();


    // hide loading
    store.commit('loading/hide');

    
    // シーン切り替え、背景ローディングはローディング後に行う
    await this.changeScene(this.viewerConfig.initialViewName);
    await this.scene.loadBackground();
    
  }

  onWindowResize(){
    this.camera.aspect = window.innerWidth / window.innerHeight;
    this.camera.updateProjectionMatrix();

    this.renderer.setSize( window.innerWidth, window.innerHeight );
    this.bFristFrame = true;
  }

  onMouseMove(e){
    this.currentMousePosition = this.getMousePosition(e);
  }

  onControlEvent(e){
    const type = e.originalEvent.type;

    // mousedown -> upが連続で起きたときはクリックとみなし、up時にraycastを行う
    if(type == 'mousedown'){
      // 選択中のオブジェクトがなければレイを飛ばす
      if(this.selectedObjects.length == 0)
        this.bWillRaycast = true;
      // 選択中のオブジェクトがあればマテリアルウインドウを表示
      else{
        store.commit('material/show');
        this.choosedObject = this.selectedObjects[0];
        this.renderer.domElement.style.cursor = 'auto'
      }
    }else if(type == 'mouseup'){
      if(this.bWillRaycast == true){
        // raycast and move hit position
        const mousePosition = this.getMousePosition(e.originalEvent);
        const intersects = this.rayCast(mousePosition);
        if(intersects[0]){
          const point = intersects[0].point;
          const camPos = this.camera.position;
          this.cameraMove(new THREE.Vector3(point.x, camPos.y, point.z));
        }
      }
    }else if(type == 'mousemove'){
      this.bWillRaycast = false;
    }
  }

  onSelectMaterial(val){
    this.bRender = !val;
  }

  cameraMove(targetPosition){
    
    const currentPosition = this.cameraControls.getPosition();
    let tmp = new THREE.Vector3();
    TweenMax.fromTo(tmp, 1, {...currentPosition}, {...targetPosition, onStart:()=>{
      this.cameraControls.enabled = false;
    }, onUpdate: ()=>{
      this.cameraControls.moveTo(tmp.x, tmp.y, tmp.z, false);
    }, onComplete: ()=>{
      this.cameraControls.enabled = true;
    }})
  }

  async changeScene(sceneName){
    const p = this.scene.getDefaultCameraPositionBySceneName(sceneName);
    this.cameraControls.moveTo(p.x, p.y, p.z, false);
    const angle = this.scene.getDefaultCameraAngleBySceneName(sceneName);
    console.log(angle)
    this.cameraControls.rotateTo(angle.x, angle.y, false); // azimath, polar, isTransitionAnim
    const bNeedUpdate = await this.scene.change(sceneName);

    if(!bNeedUpdate) return;

    
    // update internal state
    this.bFristFrame = true;
  }

  changeMaterial(materialName){
    const mat = this.materialList[`${materialName}`];
    if(mat && this.choosedObject){
      if(!this.choosedObject.replacedMaterial)
        this.choosedObject.replacedMaterial = this.choosedObject.material
      this.choosedObject.material = mat;
    }
  }

  setupRenderPass(){
    this.composer.addPass(new RenderPass(this.scene, this.camera));

    this.outlinePass = new OutlinePass( new THREE.Vector2( window.innerWidth, window.innerHeight ), this.scene, this.camera );
    this.outlinePass.edgeStrength = 3.0;
    this.composer.addPass( this.outlinePass );
  }

  rayCast(targetPos){
    this.rayCaster.setFromCamera(targetPos, this.camera);
    // calculate objects intersecting the picking ray
    const target = this.scene.getRayTargetObjects();
    const intersects = this.rayCaster.intersectObjects(target);
    return intersects;
    
  }

  getMousePosition(e){
    const clientX = e.clientX;
    const clientY = e.clientY;
    const mousePosition = new THREE.Vector2();
    const canvas = this.renderer.domElement;
    const rect = this.renderer.domElement.getBoundingClientRect();
    mousePosition.x = ((clientX - rect.left) / canvas.width) * 2 - 1;
    mousePosition.y = -((clientY - rect.top) / canvas.height) * 2 + 1;
    return mousePosition;
  }

  updateSelectedObject(obj){
    this.selectedObjects = [];
    this.selectedObjects.push(obj);
    this.outlinePass.selectedObjects = this.selectedObjects;
  }

  

  render(){
    const loop = (e)=>{
      requestAnimationFrame( loop );
      if(this.bRender == false)
        return;
      
     
      // debug control
      if(!ENABLE_CAMERA_CONTROL){
        this.orbitControls.update();
        this.composer.render();
        return;
      }

      // update controls
      const delta = this.clock.getDelta();
      const updated = this.cameraControls.update( delta );

      let bRenderFlag = false;
      // 更新がない場合マウスからのレイキャストをする
      if(!updated && this.currentMousePosition){
        const intersects = this.rayCast(this.currentMousePosition);
        if(intersects[0]){
          let bSelecting = false;
          if(intersects[0].object.touchable){
            bSelecting = true;
            this.renderer.domElement.style.cursor = 'pointer'

            // 選択中のオブジェクトがない or 変更があったら選択するオブジェクトを更新してレンダーフラグをonにする
            let bNeedUpdate = false;
            if(this.selectedObjects.length > 0){
              if(this.selectedObjects[0] != intersects[0].object){
                bNeedUpdate = true;
              }
            }else{
              bNeedUpdate = true;
            }

            if(bNeedUpdate){
              this.updateSelectedObject(intersects[0].object);
              bRenderFlag = true;
            }
          }
          
          // あたったオブジェクトが選択可能ではないor何にも当たらなかった場合
          // 前フレームで選択中のオブジェクトがあった場合は消してレンダリングをする
          if(!bSelecting &&　this.selectedObjects.length > 0){
            bRenderFlag = true;

            this.renderer.domElement.style.cursor = 'auto'
            this.selectedObjects = [];
            this.outlinePass.selectedObjects = [];
          }

          if(intersects[0].object.movable){
            this.scene.cursor.visible = true;
            const mousePos = intersects[0].point;
            this.scene.cursor.position.set(mousePos.x, mousePos.y+0.1, mousePos.z);
            bRenderFlag = true;
          }else{
            this.scene.cursor.visible = false;
            bRenderFlag = true;
          }
        }
      }

      // カメラが動いているとき/最初のフレーム/選択オブジェクトの更新があったとき -> レンダリング
      if(this.bFristFrame || updated || bRenderFlag){
        this.composer.render();
        this.bFristFrame = false;
      }
    }
    loop();
  }

}

export default new Renderer();
